// How a cell's value is shown: Excel's number formats (the part a family's
// sheets use: General, 0, 0.00, #,##0, 0%, dates and times, text in
// quotes such as #,##0"원", sections for negatives and zero, [Red]), errors
// as their text, TRUE/FALSE. A cell without a format takes one from what
// was typed, as Excel does: "2026-10-05" shows as a date, "12%" as a
// percent, "1,234" with groups, =TODAY() as a date.
import type { CalcValue } from '../calc/protocol'

export type Align = 'l' | 'c' | 'r'

export interface Shown {
  text: string
  align: Align
  /** a colour from the format ([Red]) */
  color?: string
  error?: boolean
}

/** The format presets of the toolbar. */
export const NUMBER_FORMATS: { code: string; label: string }[] = [
  { code: '', label: '일반' },
  { code: '0', label: '0' },
  { code: '0.00', label: '0.00' },
  { code: '#,##0', label: '#,##0' },
  { code: '#,##0.00', label: '#,##0.00' },
  { code: '#,##0"원"', label: '원' },
  { code: '0%', label: '0%' },
  { code: '0.0%', label: '0.0%' },
  { code: 'yyyy-mm-dd', label: '날짜' },
  { code: 'yyyy-mm-dd hh:mm', label: '날짜 시각' },
]

const COLORS: Record<string, string> = {
  red: '#d32f2f',
  blue: '#1565c0',
  green: '#2e7d32',
  magenta: '#ad1457',
  cyan: '#00838f',
  yellow: '#b8860b',
  black: '',
  white: '#ffffff',
}

/** Excel's General: up to 11 significant digits, exponent for very big or small. */
export function generalNumber(n: number): string {
  if (!Number.isFinite(n)) return '#NUM!'
  if (n === 0) return '0'
  const a = Math.abs(n)
  if (Number.isInteger(n) && a < 1e11) return String(n)
  if (a >= 1e11 || a < 1e-9) {
    const [m = '0', e = '0'] = n.toExponential(5).split('e')
    const mant = m.includes('.') ? m.replace(/0+$/, '').replace(/\.$/, '') : m
    const exp = Number(e)
    return `${mant}E${exp < 0 ? '-' : '+'}${String(Math.abs(exp)).padStart(2, '0')}`
  }
  const digits = Math.max(1, 10 - Math.max(0, Math.floor(Math.log10(a))))
  return String(Number(n.toFixed(Math.min(digits, 15))))
}

/** The format a cell takes from its input when it has none. */
export function implicitFormat(input: string): string | undefined {
  const t = input.trim()
  if (t.startsWith('=')) {
    if (/^=\s*(TODAY|DATE|EDATE|EOMONTH|DATEVALUE|WORKDAY)\s*\(/i.test(t)) return 'yyyy-mm-dd'
    if (/^=\s*NOW\s*\(/i.test(t)) return 'yyyy-mm-dd hh:mm'
    return undefined
  }
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(t)) return 'yyyy-mm-dd'
  if (/^\d{4}-\d{1,2}-\d{1,2} \d{1,2}:\d{2}(:\d{2})?$/.test(t)) return 'yyyy-mm-dd hh:mm'
  const pct = /^[+-]?[\d,]*(\.(\d+))?%$/.exec(t)
  if (pct) return pct[2] ? `0.${'0'.repeat(pct[2].length)}%` : '0%'
  const grouped = /^[+-]?\d{1,3}(,\d{3})+(\.(\d+))?$/.exec(t)
  if (grouped) return grouped[3] ? `#,##0.${'0'.repeat(grouped[3].length)}` : '#,##0'
  return undefined
}

/** Splits a format into sections at ; outside quotes. */
function sections(code: string): string[] {
  const out: string[] = []
  let cur = ''
  for (let i = 0; i < code.length; i++) {
    const ch = code[i]!
    if (ch === '"') {
      const j = code.indexOf('"', i + 1)
      const end = j < 0 ? code.length : j
      cur += code.slice(i, end + 1)
      i = end
    } else if (ch === '\\' && i + 1 < code.length) {
      cur += ch + code[i + 1]
      i++
    } else if (ch === ';') {
      out.push(cur)
      cur = ''
    } else cur += ch
  }
  out.push(cur)
  return out
}

interface Tok {
  lit?: string
  code?: string
}

/** Literal parts ("..", \x, _x, *x, [..]) and the code characters. */
function tokens(sec: string): { toks: Tok[]; color?: string } {
  const toks: Tok[] = []
  let color: string | undefined
  for (let i = 0; i < sec.length; i++) {
    const ch = sec[i]!
    if (ch === '"') {
      const j = sec.indexOf('"', i + 1)
      const end = j < 0 ? sec.length : j
      toks.push({ lit: sec.slice(i + 1, end) })
      i = end
    } else if (ch === '\\') {
      if (i + 1 < sec.length) toks.push({ lit: sec[i + 1] })
      i++
    } else if (ch === '_') {
      toks.push({ lit: ' ' })
      i++
    } else if (ch === '*') {
      i++
    } else if (ch === '[') {
      const j = sec.indexOf(']', i)
      const end = j < 0 ? sec.length : j
      const inner = sec.slice(i + 1, end)
      const c = COLORS[inner.toLowerCase()]
      if (c !== undefined) color = c || undefined
      else if (/^(h+|m+|s+)$/i.test(inner)) toks.push({ code: inner })
      i = end
    } else toks.push({ code: ch })
  }
  return { toks, color }
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

function isDateCode(toks: Tok[]): boolean {
  return toks.some((t) => t.code !== undefined && /[ymdhs]/i.test(t.code) && !/[0#?]/.test(t.code))
}

/** Excel serial (1900 system, with its 1900-02-29) to parts. */
export function serialParts(serial: number): { y: number; m: number; d: number; wd: number; hh: number; mi: number; ss: number } | null {
  if (!Number.isFinite(serial) || serial < 0 || serial >= 2958466) return null
  let days = Math.floor(serial)
  let secs = Math.round((serial - days) * 86400)
  if (secs >= 86400) {
    days++
    secs -= 86400
  }
  let y: number, m: number, d: number
  if (days === 60) {
    y = 1900
    m = 2
    d = 29
  } else {
    const ms = Date.UTC(1899, 11, days < 60 ? 31 : 30) + days * 86400000
    const dt = new Date(ms)
    y = dt.getUTCFullYear()
    m = dt.getUTCMonth() + 1
    d = dt.getUTCDate()
  }
  const wd = (((days - 1) % 7) + 7) % 7 // serial 1 (1900-01-01) is a Sunday in Excel
  return { y, m, d, wd, hh: Math.floor(secs / 3600), mi: Math.floor((secs % 3600) / 60), ss: secs % 60 }
}

function formatDate(n: number, toks: Tok[]): string | null {
  const p = serialParts(n)
  if (!p) return null
  // merge runs of the same code letter: "yyyy" -> one token
  const codes: Tok[] = []
  for (const t of toks) {
    const last = codes[codes.length - 1]
    if (t.code !== undefined && last?.code !== undefined && last.code[0]?.toLowerCase() === t.code[0]?.toLowerCase() && /[ymdhs]/i.test(t.code))
      last.code += t.code
    else codes.push({ ...t })
  }
  const ampm = codes.some((t) => t.code !== undefined && /^[aA]$/.test(t.code)) || toks.map((t) => t.code ?? '').join('').toUpperCase().includes('AM/PM')
  let out = ''
  for (let i = 0; i < codes.length; i++) {
    const t = codes[i]!
    if (t.lit !== undefined) {
      out += t.lit
      continue
    }
    const c = t.code!
    const low = c.toLowerCase()
    const two = (v: number) => String(v).padStart(2, '0')
    // m after h or before s is minutes
    const prev = codes.slice(0, i).reverse().find((x) => x.code !== undefined && /[a-z]/i.test(x.code))
    const next = codes.slice(i + 1).find((x) => x.code !== undefined && /[a-z]/i.test(x.code))
    const minutes = low[0] === 'm' && low.length <= 2 && ((prev?.code ?? '').toLowerCase().startsWith('h') || (next?.code ?? '').toLowerCase().startsWith('s'))
    if (low.startsWith('y')) out += low.length <= 2 ? two(p.y % 100) : String(p.y)
    else if (minutes) out += low.length === 2 ? two(p.mi) : String(p.mi)
    else if (low.startsWith('m')) out += low.length === 1 ? String(p.m) : low.length === 2 ? two(p.m) : low.length === 3 ? MONTHS[p.m - 1]!.slice(0, 3) : MONTHS[p.m - 1]!
    else if (low.startsWith('d')) out += low.length === 1 ? String(p.d) : low.length === 2 ? two(p.d) : low.length === 3 ? DAYS[p.wd]!.slice(0, 3) : DAYS[p.wd]!
    else if (low.startsWith('h')) {
      const h = ampm ? ((p.hh + 11) % 12) + 1 : p.hh
      out += low.length >= 2 ? two(h) : String(h)
    } else if (low.startsWith('s')) out += low.length >= 2 ? two(p.ss) : String(p.ss)
    else if (low === 'a' || low === '/' || low === 'p') {
      // AM/PM: written once at its "A"
      if (c === 'A' || c === 'a') {
        out += p.hh < 12 ? 'AM' : 'PM'
        // skip "M/PM"
        let j = i + 1
        let skip = 'M/PM'
        while (skip.length && j < codes.length) {
          const s = codes[j]!.code ?? codes[j]!.lit ?? ''
          if (skip.toUpperCase().startsWith(s.toUpperCase())) {
            skip = skip.slice(s.length)
            j++
          } else break
        }
        i = j - 1
      } else out += c
    } else out += c
  }
  return out
}

function group(int: string): string {
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

function formatNumberSection(n: number, toks: Tok[], negInFormat: boolean): string {
  const code = toks.map((t) => t.code ?? '').join('')
  if (/^\s*general\s*$/i.test(code) || toks.every((t) => t.code === undefined || !/[0#?.,%Ee]/.test(t.code))) {
    // General, maybe with text around it
    const g = /general/i.test(code)
    let out = ''
    let wrote = false
    const lits = toks.map((t) => t.lit ?? t.code ?? '').join('')
    if (g) {
      const i = lits.toLowerCase().indexOf('general')
      out = lits.slice(0, i) + generalNumber(negInFormat ? Math.abs(n) : n) + lits.slice(i + 7)
      wrote = true
    }
    return wrote ? out : lits
  }
  const percent = (code.match(/%/g) ?? []).length
  let v = Math.abs(n) * 100 ** percent
  // the numeric part: from the first to the last placeholder
  const first = toks.findIndex((t) => t.code !== undefined && /[0#?]/.test(t.code))
  let last = -1
  toks.forEach((t, i) => {
    if (t.code !== undefined && /[0#?.,]/.test(t.code) && i >= first) {
      if (/[0#?]/.test(t.code)) last = i
    }
  })
  if (first < 0) return toks.map((t) => t.lit ?? t.code ?? '').join('')
  // a "." right after the last digit
  const num = toks.slice(first, last + 1).map((t) => t.code ?? '').join('')
  // commas after the last placeholder scale by 1000
  let j = last + 1
  while (j < toks.length && toks[j]!.code === ',') {
    v /= 1000
    j++
  }
  const sci = /^([0#?.,]+)E([+-])([0#]+)$/i.exec(toks.slice(first).map((t) => t.code ?? t.lit ?? '').join('').replace(/[^0#?.,Ee+-].*$/, ''))
  let body: string
  if (sci) {
    const [ip = '', dp = ''] = (sci[1] ?? '').split('.')
    const dec = dp.length
    const exp = v === 0 ? 0 : Math.floor(Math.log10(v)) - (Math.max(1, ip.replace(/,/g, '').length) - 1)
    const mant = v / 10 ** exp
    body = `${mant.toFixed(dec)}E${exp < 0 ? '-' : sci[2] === '+' ? '+' : ''}${String(Math.abs(exp)).padStart((sci[3] ?? '').length, '0')}`
    j = toks.length
    const after = toks.slice(first).findIndex((t) => /[eE]/.test(t.code ?? ''))
    if (after >= 0) {
      // skip the exponent placeholders
      let k = first + after + 1
      while (k < toks.length && /[+\-0#]/.test(toks[k]!.code ?? '')) k++
      j = k
    }
  } else {
    const [ip = '', dp = ''] = num.split('.')
    const decMax = (dp.match(/[0#?]/g) ?? []).length
    const decMin = (dp.match(/0/g) ?? []).length
    const intMin = (ip.match(/0/g) ?? []).length
    const grouping = ip.includes(',')
    let s = v.toFixed(decMax)
    let [is = '0', ds = ''] = s.split('.')
    while (ds.length > decMin && ds.endsWith('0')) ds = ds.slice(0, -1)
    if (is === '0' && intMin === 0) is = ''
    is = is.padStart(intMin, '0')
    if (grouping) is = group(is)
    s = is + (num.includes('.') && (ds.length > 0 || dp.length > 0) ? '.' + ds : '')
    body = s
  }
  const sign = n < 0 && !negInFormat ? '-' : ''
  const before = toks
    .slice(0, first)
    .map((t) => t.lit ?? (t.code === '%' ? '%' : t.code ?? ''))
    .join('')
  const after = toks
    .slice(j)
    .map((t) => t.lit ?? (t.code === ',' ? '' : t.code ?? ''))
    .join('')
  // a minus goes before the number, after a currency sign written first
  return sign && before && !/^[\s(]*$/.test(before) ? before + sign + body + after : sign + before + body + after
}

/** A number in an Excel format. */
export function formatNumber(n: number, code: string): { text: string; color?: string } {
  if (!Number.isFinite(n)) return { text: '#NUM!' }
  if (code === '' || /^general$/i.test(code)) return { text: generalNumber(n) }
  const secs = sections(code)
  let sec = secs[0] ?? ''
  let negInFormat = false
  if (n < 0 && secs.length >= 2) {
    sec = secs[1] ?? ''
    negInFormat = true
  } else if (n === 0 && secs.length >= 3) {
    sec = secs[2] ?? ''
  }
  const { toks, color } = tokens(sec)
  if (isDateCode(toks)) {
    const d = formatDate(n, toks)
    return d === null ? { text: '#'.repeat(8), ...(color ? { color } : {}) } : { text: d, ...(color ? { color } : {}) }
  }
  return { text: formatNumberSection(n, toks, negInFormat), ...(color ? { color } : {}) }
}

/** What a cell shows. */
export function formatValue(v: CalcValue | undefined, nf: string | undefined, input = ''): Shown {
  if (v === null || v === undefined) return { text: '', align: 'l' }
  if (typeof v === 'object') return { text: v.error, align: 'c', error: true }
  if (typeof v === 'boolean') return { text: v ? 'TRUE' : 'FALSE', align: 'c' }
  if (typeof v === 'string') {
    const secs = nf ? sections(nf) : []
    const textSec = secs.length >= 4 ? secs[3] : undefined
    if (textSec !== undefined && textSec.includes('@')) {
      const { toks } = tokens(textSec)
      return { text: toks.map((t) => (t.code === '@' ? v : (t.lit ?? t.code ?? ''))).join(''), align: 'l' }
    }
    return { text: v, align: 'l' }
  }
  const code = nf && nf !== '' ? nf : (implicitFormat(input) ?? '')
  const r = formatNumber(v, code)
  return { text: r.text, align: 'r', ...(r.color ? { color: r.color } : {}) }
}
